import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import InputField from "../../../components/InputField";
import Button from "../../../components/Button";
import BackButton from "../../../components/BackButton";
import api from "../../../api/api";
import SaveIcon from "@mui/icons-material/Save";
import Select from "react-select";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import "../../form.css";
import SelectField from "../../../components/SelectField";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExchangeRateEditModal from "../ExchangeRateEditModal";

const InvoicingForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editSaleInvoiceId = searchParams.get("editSaleInvoiceId")
    ? parseInt(searchParams.get("editSaleInvoiceId"), 10)
    : (location.state?.editSaleInvoiceId || null);
  const editInvoiceSectionIdx = searchParams.get("editInvoiceSectionIdx")
    ? parseInt(searchParams.get("editInvoiceSectionIdx"), 10)
    : null;
  const isSingleInvoiceEdit = Boolean(editSaleInvoiceId && editInvoiceSectionIdx !== null);
  
  // DEBUG: Log the edit params
  console.log("EDIT PARAMS:", { editSaleInvoiceId, editInvoiceSectionIdx, isSingleInvoiceEdit, search: window.location.search });

  // Form state
  const [formData, setFormData] = useState({
    job_number: "",
    lut_number: "",
    inv_number: "",
    bl_number: "",
    other_reference: "",
    invoice_date: "",
    billing_party: "",
    shipper: "",
    shipper_address: "",
    consignee: "",
    consignee_address: "",
    notify_party1: "",
    notify_party1_address: "",
    notify_party2: "",
    notify_party2_address: "",
    pol: "",
    pod: "",
    place_of_receipt: "",
    final_port_of_delivery: "",
    transshipment_hub: "",
    shipment_segment: "",
    credit_days: "",
    credit_days_system: "",
    incoterms: "",
    commodity_name: "",
    freight_terms: "",
    gross_weight: "",
    number_of_packages: "",
    cbm: "",
    chargeable_weight: "",
    container_number: "",
    airline_name: "",
    consolidation_at: "",
    vessel_name: "",
    voyage_number: "",
    payment_ref: "",
    payment_date: "",
    term_and_condition: "",
  });

  const [portsOptions, setPorts] = useState([]);
  const routing_options = ["Direct", "Indirect", "Both"];
  const freight_terms_options = ["Prepaid", "Collect", "All Collect"];

  // Freight terms historically stored free text (e.g. "PREPAID"). Map any
  // legacy casing/spelling onto the dropdown's exact option so the Collect
  // split logic below and the invoice PDF both see one canonical value.
  const normalizeFreightTerms = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const match = freight_terms_options.find(
      (opt) => opt.toLowerCase() === raw.toLowerCase(),
    );
    if (match) return match;
    // "All Collect" must be tested before "Collect": it contains the
    // word collect, so the order matters.
    if (/all\s*collect/i.test(raw)) return "All Collect";
    // Treat anything containing "collect" as Collect, otherwise Prepaid, so
    // legacy rows are not silently reclassified to the wrong party.
    return /collect/i.test(raw) ? "Collect" : "Prepaid";
  };

  // Stable per-row identity so the Collect split survives re-renders.
  // chargeRow rows have no server id, so the list position is used.
  const chargeRowKey = (index) => `charge_${index}`;

  const needsCollectSplit = (value) =>
    String(value || "").trim().toLowerCase() === "collect"
    && String(value || "").trim().toLowerCase() !== "all collect";

  const needsInvoiceSections = (value) => {
    const v = String(value || "").trim().toLowerCase();
    return v === "collect" || v === "prepaid" || v === "all collect";
  };

  const canAddMultipleInvoices = (value) =>
    String(value || "").trim().toLowerCase() === "collect";

  // Label + INR value per row, for the Collect modal's charge list.
  const collectChargeLabel = (row) => {
    const code =
      typeof row.charge_code === "object"
        ? row.charge_code?.label || row.charge_code?.value || ""
        : row.charge_code || "";
    const amount = Number(row.amount_in_inr || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${code} — ${amount}`;
  };

  // Recalculate amount, amount_in_inr, gst_amount for a charge row
  const calculateChargeValues = (row) => {
    const rate = parseFloat(row.rate) || 0;
    const qty = parseFloat(row.quantity) || 0;
    const exchange = parseFloat(row.exchange_rate) || 1;
    const amount = rate * qty;

    let amountInInr;
    if (!row.currency || row.currency === "INR") {
      amountInInr = amount;
    } else {
      amountInInr = amount * exchange;
    }

    const isGstEnabled = row.gst_option?.value === "yes";
    const gstPercentage = isGstEnabled ? parseFloat(row.gst_percentage) || 0 : 0;
    const gstAmount = isGstEnabled ? (amountInInr * gstPercentage) / 100 : 0;

    return {
      amount: Number(amount.toFixed(2)),
      amount_in_inr: Number(amountInInr.toFixed(2)),
      gst_amount: Number(gstAmount.toFixed(2)),
    };
  };

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        setLoadingPorts(true);
        const response = await api.get("/ports");
        const allPorts = response.data;
        setPorts(allPorts);
      } catch (err) {
        console.error("Failed to fetch ports:", err);
      } finally {
        setLoadingPorts(false);
      }
    };

    fetchPorts();
  }, []);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPorts, setLoadingPorts] = useState(true);
  const [chargeCodeOptions, setChargeCodeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingChargeCodes, setLoadingChargeCodes] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [gstPercentageCache, setGstPercentageCache] = useState({});
  const [sacCodeCache, setSacCodeCache] = useState({});
  const [loadingGstForCharge, setLoadingGstForCharge] = useState(false);
  const [containerTableData, setContainerTableData] = useState([]);
  const [inputChargeRow, setInputChargeRow] = useState({
    charge_code: "",
    sac_code: "",
    default_gst_percentage: 0,
    currency: "",
    exchange_rate: 1,
    rate: 0,
    quantity: 0,
    amount: 0,
    amount_in_inr: 0,
    gst_option: null,
    gst_percentage: 0,
    gst_amount: 0,
  });
  const [chargeRow, setChargeRow] = useState([]);
  const [quotationCharges, setQuotationCharges] = useState([]);

  const [totals, setTotals] = useState({
    subTotal: 0,
    gstAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    grandTotal: 0,
  });

  const [editedExchangeRates, setEditedExchangeRates] = useState({});

  const handleExchangeRateChange = (currency, value) => {
    const rate = parseFloat(value);

    // Temporary form state only.
    // This does NOT update Exchange Rate Master / database.
    setEditedExchangeRates((prev) => ({
      ...prev,
      [currency]: value,
    }));

    if (!currency || currency === "INR") {
      return;
    }

    if (Number.isNaN(rate)) {
      return;
    }

    // Update every charge having this currency
    setChargeRow((prev) =>
      prev.map((row) => {
        if (row.currency !== currency) {
          return row;
        }

        const updatedRow = { ...row, exchange_rate: rate };
        const calc = calculateChargeValues(updatedRow);
        return { ...updatedRow, ...calc };
      }),
    );

    // Also update the input row if same currency is selected
    if (inputChargeRow.currency === currency) {
      handleInputChargeChange("exchange_rate", rate);
    }
  };

  // Auto-recalculate totals whenever chargeRow changes (add/delete)
  useEffect(() => {
    const subTotal = chargeRow.reduce(
      (acc, row) => acc + Number(row.amount_in_inr || 0),
      0,
    );
    const gstAmount = chargeRow.reduce((acc, row) => {
      return (
        acc +
        (row.gst_option?.value === "yes" ? Number(row.gst_amount || 0) : 0)
      );
    }, 0);

    const convertInrToInvoiceCurrency = (amountInInr, currency) => {
      const amount = Number(amountInInr) || 0;

      if (!currency || currency === "INR") {
        return Number(amount.toFixed(2));
      }

      const exchangeRate = Number(editedExchangeRates[currency]);

      if (!exchangeRate || exchangeRate <= 0) {
        return 0;
      }

      return Number((amount / exchangeRate).toFixed(2));
    };

    setTotals({
      subTotal: Number(subTotal.toFixed(2)),
      gstAmount: Number(gstAmount.toFixed(2)),
      cgstAmount: Number((gstAmount / 2).toFixed(2)),
      sgstAmount: Number((gstAmount / 2).toFixed(2)),
      grandTotal: Number((subTotal + gstAmount).toFixed(2)),
    });
  }, [chargeRow]);

  const [errors, setErrors] = useState({
    form: {},
    containerTable: "",
    chargeTable: "",
    invoiceSplit: "",
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceType, setInvoiceType] = useState(null); // "performa" or "final"

  const [dateOfIssue, setDateOfIssue] = useState(null);
  const [dateOfIssueError, setDateOfIssueError] = useState("");
  const [inrPerUsd, setInrPerUsd] = useState(0);
  const [showExchangeRateModal, setShowExchangeRateModal] = useState(false);
  const [selectedExchangeCurrency, setSelectedExchangeCurrency] = useState("");
  const [exchangeRateModalValue, setExchangeRateModalValue] = useState("");

  // Collect split: user creates invoice sections and assigns charges.
  const [invoices, setInvoices] = useState([]);
  const [showChargeSelectModal, setShowChargeSelectModal] = useState(false);
  const [chargeSelectInvoiceId, setChargeSelectInvoiceId] = useState(null);
  const [chargeSelectError, setChargeSelectError] = useState("");

  // Compute totals for an arbitrary charge subset.
  const computeInvoiceTotals = (charges = [], currency = "INR") => {
    const isINR = currency === "INR";
    const subTotal = charges.reduce(
      (acc, row) => {
        const c = row.charge || row;
        const amt = isINR ? Number(c.amount_in_inr || 0) : Number(c.amount || 0);
        return acc + amt;
      },
      0,
    );
    const gstAmount = isINR ? charges.reduce((acc, row) => {
      const c = row.charge || row;
      const base = Number(c.amount_in_inr || 0);
      const gstPct = Number(c.gst_percentage || 0);
      const gstAmt = c.gst_option?.value === "yes" ? (base * gstPct) / 100 : 0;
      return acc + gstAmt;
    }, 0) : 0;

    return {
      subTotal: Number(subTotal.toFixed(2)),
      gstAmount: Number(gstAmount.toFixed(2)),
      cgstAmount: Number((gstAmount / 2).toFixed(2)),
      sgstAmount: Number((gstAmount / 2).toFixed(2)),
      grandTotal: Number((subTotal + gstAmount).toFixed(2)),
    };
  };




  const convertInrToInvoiceCurrency = (amountInInr, currency) => {
    const amount = Number(amountInInr) || 0;

    if (!currency || currency === "INR") {
      return Number(amount.toFixed(2));
    }

    const exchangeRate = Number(editedExchangeRates[currency]);

    if (!exchangeRate || exchangeRate <= 0) {
      return 0;
    }

    return Number((amount / exchangeRate).toFixed(2));
  };




  // Get charges for a specific invoice based on charge_keys.
  const getInvoiceCharges = (invoice) => {
    if (!invoice || !Array.isArray(invoice.charge_keys)) return [];
    return invoice.charge_keys
      .map((key) => {
        const index = parseInt(key.replace("charge_", ""), 10);
        const charge = chargeRow[index] || null;
        return charge ? { charge, key } : null;
      })
      .filter(Boolean);
  };

  // All charge keys currently assigned to any invoice.
  const getAllAssignedKeys = () => {
    return invoices.flatMap((inv) => inv.charge_keys || []);
  };

  const isChargeAssigned = (chargeKey) => {
    return getAllAssignedKeys().includes(chargeKey);
  };

  const addInvoice = () => {
    const newId = `inv_${Date.now()}`;
    setInvoices((prev) => [
      ...prev,
      {
        id: newId,
        invoice_number: "",
        invoice_date: "",
        billing_party: "Shipper",
        currency: "INR",
        notify_party2: "",
        notify_party2_address: "",
        charge_keys: [],
        inputChargeRow: {
          charge_code: "",
          sac_code: "",
          default_gst_percentage: 0,
          currency: "",
          exchange_rate: 1,
          rate: 0,
          quantity: 0,
          amount: 0,
          amount_in_inr: 0,
          gst_option: null,
          gst_percentage: 0,
          gst_amount: 0,
        },
      },
    ]);
  };

  const removeInvoice = (id) => {
    setInvoices((prev) => prev.filter((inv) => inv.id !== id));
  };

  const updateInvoice = (id, field, value) => {
    setInvoices((prev) =>
      prev.map((inv) => {
        if (inv.id !== id) return inv;
        const updated = { ...inv, [field]: value };
        // When billing party changes to "Notify Party 2", prefill from formData
        if (field === "billing_party" && value === "Notify Party 2") {
          updated.notify_party2 = formData.notify_party2 || "";
          updated.notify_party2_address = formData.notify_party2_address || "";
        }
        return updated;
      }),
    );
  };

  const addChargeToInvoice = (invoiceId, chargeKey) => {
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, charge_keys: [...(inv.charge_keys || []), chargeKey] }
          : inv,
      ),
    );
  };

  const removeChargeFromInvoice = (invoiceId, chargeKey) => {
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, charge_keys: (inv.charge_keys || []).filter((k) => k !== chargeKey) }
          : inv,
      ),
    );
  };

  const openChargeSelectModal = (invoiceId) => {
    setChargeSelectInvoiceId(invoiceId);
    setChargeSelectError("");
    setShowChargeSelectModal(true);
  };

  const updateInvoiceInputCharge = (invoiceId, field, value) => {
    setInvoices((prev) =>
      prev.map((inv) => {
        if (inv.id !== invoiceId) return inv;
        const input = { ...inv.inputChargeRow, [field]: value };

        // If currency changes to non-INR, disable GST
        if (field === "currency" && value !== "INR") {
          input.gst_option = null;
          input.gst_percentage = 0;
          input.gst_amount = 0;
        }

        // If GST option toggled, set GST percentage accordingly
        if (field === "gst_option") {
          if (value?.value === "yes") {
            const cachedGst = gstPercentageCache[input.charge_code];
            input.gst_percentage = cachedGst !== undefined ? cachedGst : input.default_gst_percentage || 0;
          } else {
            input.gst_percentage = 0;
          }
        }

        // Recalculate amounts for relevant field changes
        const recalcFields = ["rate", "quantity", "currency", "exchange_rate", "gst_option", "gst_percentage"];
        if (recalcFields.includes(field)) {
          const calc = calculateChargeValues(input);
          Object.assign(input, calc);
        }

        return { ...inv, inputChargeRow: input };
      }),
    );
  };

  const addInvoiceChargeRow = (invoiceId) => {
    setInvoices((prev) => {
      const updated = prev.map((inv) => {
        if (inv.id !== invoiceId) return inv;
        const input = inv.inputChargeRow || {};

        if (!input.charge_code) {
          setErrors((e) => ({ ...e, chargeTable: "Charge code is required" }));
          return inv;
        }

        const finalGstPercentage =
          input.gst_option?.value === "yes"
            ? gstPercentageCache[input.charge_code] !== undefined
              ? gstPercentageCache[input.charge_code]
              : input.gst_percentage || 0
            : 0;

        // Use helper to calculate amounts
        const chargeForCalc = {
          ...input,
          gst_percentage: finalGstPercentage,
        };
        const calc = calculateChargeValues(chargeForCalc);

        const newCharge = {
          ...input,
          gst_percentage: finalGstPercentage,
          amount: calc.amount,
          amount_in_inr: calc.amount_in_inr,
          gst_amount: calc.gst_amount,
        };

        const newIndex = chargeRow.length;
        const newKey = chargeRowKey(newIndex);
        const updatedChargeRow = [...chargeRow, newCharge];

        setChargeRow(updatedChargeRow);

        return {
          ...inv,
          charge_keys: [...(inv.charge_keys || []), newKey],
          inputChargeRow: {
            charge_code: "",
            sac_code: "",
            default_gst_percentage: 0,
            currency: "",
            exchange_rate: 1,
            rate: 0,
            quantity: 0,
            amount: 0,
            amount_in_inr: 0,
            gst_option: null,
            gst_percentage: 0,
            gst_amount: 0,
          },
        };
      });

      return updated;
    });

    setErrors((prev) => ({ ...prev, chargeTable: "" }));
  };

  useEffect(() => {
    if (!needsInvoiceSections(formData.freight_terms)) {
      setInvoices([]);
    } else if (needsInvoiceSections(formData.freight_terms) && invoices.length === 0) {
      const newId = `inv_${Date.now()}`;
      setInvoices([
        {
          id: newId,
          invoice_number: "",
          invoice_date: "",
          billing_party: "Shipper",
          currency: "INR",
          notify_party2: formData.notify_party2 || "",
          notify_party2_address: formData.notify_party2_address || "",
          charge_keys: [],
          inputChargeRow: {
            charge_code: "",
            sac_code: "",
            default_gst_percentage: 0,
            currency: "",
            exchange_rate: 1,
            rate: 0,
            quantity: 0,
            amount: 0,
            amount_in_inr: 0,
            gst_option: null,
            gst_percentage: 0,
            gst_amount: 0,
          },
        },
      ]);
    }
  }, [formData.freight_terms]);

  const handleEditExchangeRate = (currency) => {
    if (!currency || currency === "INR") {
      return;
    }

    const charge = chargeRow.find((row) => row.currency === currency);

    const currentRate =
      editedExchangeRates[currency] ?? charge?.exchange_rate ?? "";

    setSelectedExchangeCurrency(currency);
    setExchangeRateModalValue(String(currentRate));
    setShowExchangeRateModal(true);
  };

  const handleSaveExchangeRate = () => {
    const rate = parseFloat(exchangeRateModalValue);

    if (!selectedExchangeCurrency || Number.isNaN(rate) || rate <= 0) {
      return;
    }

    handleExchangeRateChange(selectedExchangeCurrency, rate);

    setShowExchangeRateModal(false);
  };

  const handleContainerChange = (index, field, value) => {
    setContainerTableData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Pre-fill the form from an existing sale invoice (Edit flow)
  const loadInvoiceForEdit = async (saleInvoiceId) => {
    if (!saleInvoiceId) return;
    try {
      console.log("LOADING INVOICE FOR EDIT:", saleInvoiceId);
      const response = await api.get(`/sale-invoices/${saleInvoiceId}`);
      const inv = response.data?.invoice || response.data;
      console.log("BACKEND RESPONSE:", inv);
      if (!inv) return;

      // Shared fields now live in enquiry (returned as inv.enquiry)
      const enquiry = inv.enquiry || {};

      setFormData({
        ...formData,
        job_number: inv.job_number || enquiry.job_number || "",
        lut_number: inv.lut_number || "",
        inv_number: inv.inv_number || "",
        bl_number: enquiry.BL_id || "",
        other_reference:
          enquiry.commercial_invoice_number || enquiry.other_reference || "",
        invoice_date: inv.invoice_date || "",
        billing_party: inv.billing_party || "",
        shipper: enquiry.first_shipper_name || "",
        shipper_address: enquiry.first_shipper_address || "",
        consignee: enquiry.first_consignee_name || "",
        consignee_address: enquiry.first_consignee_address || "",
        notify_party1: enquiry.notify_party_1 || "",
        notify_party1_address: enquiry.notify_party_1_address || "",
        notify_party2: enquiry.notify_party_2 || "",
        notify_party2_address: enquiry.notify_party_2_address || "",
        pol: enquiry.pol || "",
        pod: enquiry.pod || "",
        place_of_receipt: enquiry.place_of_receipt || "",
        final_port_of_delivery: enquiry.final_port_of_delivery || "",
        transshipment_hub: enquiry.transshipment_hub || "",
        shipment_segment: enquiry.shipment_segment || "",
        incoterms: enquiry.incoterm || "",
        commodity_name: enquiry.hs_description || "",
        freight_terms: normalizeFreightTerms(enquiry.freight_terms),
        gross_weight: enquiry.gross_weight || "",
        number_of_packages: enquiry.packaging_count || "",
        credit_days: enquiry.credit_days || "",
        credit_days_system: enquiry.credit_days_system || "",
        cbm: "",
        chargeable_weight: "",
        airline_name: "",
        consolidation_at: enquiry.stuffing_destuffing_At || "",
        vessel_name: enquiry.vessel_name || "",
        voyage_number: enquiry.voyage_number || "",
        routing: enquiry.routing || "",
        payment_ref: inv.payment_ref || "",
        payment_date: inv.payment_date || "",
        term_and_condition: inv.term_and_condition || "",
      });

      if (inv.invoice_date) {
        const parsedDate = new Date(inv.invoice_date);
        if (!Number.isNaN(parsedDate.getTime())) {
          setDateOfIssue(parsedDate);
        }
      }

      setTotals({
        subTotal: Number(inv.sub_total) || 0,
        gstAmount: Number(inv.gst_total) || 0,
        cgstAmount: (Number(inv.gst_total) || 0) / 2,
        sgstAmount: (Number(inv.gst_total) || 0) / 2,
        grandTotal: Number(inv.grand_total) || 0,
      });

      // Load invoice-level exchange rate for "Amount in Invoice Currency" display
      if (inv.currency && inv.currency !== "INR" && inv.exchange_rate) {
        setEditedExchangeRates((prev) => ({
          ...prev,
          [inv.currency]: Number(inv.exchange_rate),
        }));
      }

      let loadedChargeRow = [];
      if (Array.isArray(inv.charges) && inv.charges.length > 0) {
        loadedChargeRow = inv.charges.map((c) => ({
          charge_id: c.charge_id || "",
          charge_code: c.charge_code || "",
          sac_code: c.sac_code || "",
          currency: c.currency || "",
          exchange_rate: Number(c.exchange_rate) || 1,
          rate: Number(c.rate) || 0,
          quantity: Number(c.quantity) || 0,
          amount: Number(c.amount) || 0,
          amount_in_inr: Number(c.amount_in_inr) || 0,
          amount_in_invoice_currency: c.amount_in_invoice_currency !== undefined
            ? Number(c.amount_in_invoice_currency)
            : (c.currency && c.currency !== "INR" && c.exchange_rate
              ? Number((Number(c.amount_in_inr) / Number(c.exchange_rate)).toFixed(2))
              : Number(c.amount_in_inr)),
          gst_option:
            c.gst_option === "yes" || c.gst_option === "no"
              ? {
                value: c.gst_option,
                label: c.gst_option === "yes" ? "Yes" : "No",
              }
              : c.gst_option || null,
          gst_percentage: Number(c.gst_percentage) || 0,
          gst_amount: Number(c.gst_amount) || 0,
        }));
      }
      console.log("SETTING CHARGEROW:", { count: loadedChargeRow.length, ids: loadedChargeRow.map(c => c.charge_id) });
      setChargeRow(loadedChargeRow);

      if (Array.isArray(inv.containers) && inv.containers.length > 0) {
        setContainerTableData(inv.containers);
      }

      // ===== SINGLE INVOICE EDIT: Reconstruct section from backend (not draft) =====
      if (isSingleInvoiceEdit && editInvoiceSectionIdx !== null) {
        try {
          // The backend response already contains ONLY this invoice's charges
          // Use those directly - no need for draft's charge_keys which reference global indices
          console.log("LOADED CHARGE ROW:", loadedChargeRow);
          const targetSection = {
            id: `inv_${Date.now()}`,
            invoice_number: inv.inv_number || "",
            invoice_date: inv.invoice_date || "",
            billing_party: inv.billing_party || "Shipper",
            currency: inv.currency || "INR",
            notify_party2: "",
            notify_party2_address: "",
            // Backend charges are already filtered for this invoice, so use sequential indices
            charge_keys: loadedChargeRow.map((_, i) => `charge_${i}`),
          };
          console.log("TARGET SECTION:", targetSection);
          
          // Set invoices state with ONLY the target section
          setInvoices([
            {
              id: targetSection.id,
              invoice_number: targetSection.invoice_number,
              invoice_date: targetSection.invoice_date,
              billing_party: targetSection.billing_party,
              currency: targetSection.currency,
              notify_party2: targetSection.notify_party2,
              notify_party2_address: targetSection.notify_party2_address,
              charge_keys: targetSection.charge_keys,
              inputChargeRow: {
                charge_code: "",
                sac_code: "",
                default_gst_percentage: 0,
                currency: "",
                exchange_rate: 1,
                rate: 0,
                quantity: 0,
                amount: 0,
                amount_in_inr: 0,
                gst_option: null,
                gst_percentage: 0,
                gst_amount: 0,
              },
            },
          ]);
          console.log("Single invoice edit: loaded section from backend", targetSection);
        } catch (e) {
          console.warn("Could not reconstruct invoice section:", e);
          // Final fallback - same logic
          setInvoices([
            {
              id: `inv_${Date.now()}`,
              invoice_number: inv.inv_number || "",
              invoice_date: inv.invoice_date || "",
              billing_party: inv.billing_party || "Shipper",
              currency: inv.currency || "INR",
              notify_party2: "",
              notify_party2_address: "",
              charge_keys: loadedChargeRow.map((_, i) => `charge_${i}`),
              inputChargeRow: {
                charge_code: "",
                sac_code: "",
                default_gst_percentage: 0,
                currency: "",
                exchange_rate: 1,
                rate: 0,
                quantity: 0,
                amount: 0,
                amount_in_inr: 0,
                gst_option: null,
                gst_percentage: 0,
                gst_amount: 0,
              },
            },
          ]);
        }
      }
    } catch (err) {
      console.error("Failed to load invoice for edit:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchPorts();
      if (id && id !== "new") {
        await fetchEnquiryData(id);
      } else {
        setLoadingData(false);
      }
      // Restore user-entered draft (survives Back navigation from preview page)
      restoreDraft();
    };
    loadData();
  }, [id]);

  // Load existing invoice for editing (Edit flow from List of Transactions modal)
  useEffect(() => {
    if (editSaleInvoiceId) {
      loadInvoiceForEdit(editSaleInvoiceId);
    }
  }, [editSaleInvoiceId]);
  const fetchGSTForChargeCode = async (charge_code, index) => {
    if (!charge_code) return;

    try {
      setLoadingGstForCharge(true);
      const response = await api.get(`/charge_code/gst/${charge_code}`);
      const gstPercentage = response.data.tax_percentage || 0;
      const sacCode = response.data.sac_code || "";

      // Cache the result
      setGstPercentageCache((prev) => ({
        ...prev,
        [charge_code]: gstPercentage,
      }));
      setSacCodeCache((prev) => ({ ...prev, [charge_code]: sacCode }));

      if (index === "input") {
        setInputChargeRow((prev) => {
          // Only apply if this charge code is still currently selected
          if (prev.charge_code !== charge_code) {
            return prev;
          }

          const isGstEnabled = prev.gst_option?.value === "yes";
          const activeGstRate = isGstEnabled ? gstPercentage : 0;

          const updated = {
            ...prev,
            sac_code: sacCode,
            default_gst_percentage: gstPercentage,
            gst_percentage: activeGstRate,
          };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else if (typeof index === "string" && index.startsWith("inv_")) {
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== index) return inv;
            const input = inv.inputChargeRow;
            if (input.charge_code !== charge_code) return inv;

            const isGstEnabled = input.gst_option?.value === "yes";
            const activeGstRate = isGstEnabled ? gstPercentage : 0;

            const updated = {
              ...input,
              sac_code: sacCode,
              default_gst_percentage: gstPercentage,
              gst_percentage: activeGstRate,
            };
            const calc = calculateChargeValues(updated);
            return {
              ...inv,
              inputChargeRow: { ...updated, ...calc },
            };
          }),
        );
      }

      // Also update any rows in chargeRow that have this charge_code and GST enabled
      setChargeRow((prev) =>
        prev.map((row) => {
          if (
            row.charge_code === charge_code &&
            row.currency === "INR" &&
            row.gst_option?.value === "yes"
          ) {
            const updated = {
              ...row,
              sac_code: sacCode,
              default_gst_percentage: gstPercentage,
              gst_percentage: gstPercentage,
            };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          }
          return row;
        }),
      );
    } catch (err) {
      console.error("Failed to fetch GST for charge code:", err);
    } finally {
      setLoadingGstForCharge(false);
    }
  };

  const handleCurrencyChange = async (index, selectedCurrency) => {
    const country_code = selectedCurrency?.value || "";

    if (!country_code) {
      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, currency: "", exchange_rate: "" };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else {
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: "", exchange_rate: "" };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      }
      return;
    }

    // INR = base currency
    if (country_code === "INR") {
      setEditedExchangeRates((prev) => ({ ...prev, INR: 1 }));

      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, currency: "INR", exchange_rate: 1 };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else {
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: "INR", exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      }
      return;
    }

    // Use cached rate if available
    if (editedExchangeRates[country_code] !== undefined) {
      const editedRate = Number(editedExchangeRates[country_code]);

      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, currency: country_code, exchange_rate: editedRate };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else {
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: editedRate };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      }
      return;
    }

    // Fetch exchange rate from API first
    try {
      const res = await api.get(`/exchange_rate/rate/${country_code}`);
      const exchangeRateFromApi =
        res.data?.rate_in_NRO || res.data?.rate_in_INR || 1;
      const exchangeRate = Number(exchangeRateFromApi);

      // Store in cache
      setEditedExchangeRates((prev) => ({
        ...prev,
        [country_code]: exchangeRate,
      }));

      // Update both currency and exchange_rate together with correct calculation
      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, currency: country_code, exchange_rate: exchangeRate };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else {
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: exchangeRate };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);

      // Fallback: set currency with exchange_rate = 1
      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, currency: country_code, exchange_rate: 1 };
          const calc = calculateChargeValues(updated);
          return { ...updated, ...calc };
        });
      } else {
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      }
    }
  };
  
  // Handle currency change for invoice section charges (both input row and existing rows)
  const handleInvoiceCurrencyChange = async (invoiceId, chargeKey, country_code) => {
    if (!country_code) {
      // Clear currency and exchange_rate
      if (chargeKey) {
        // Existing charge row in chargeRow
        const index = parseInt(chargeKey.replace("charge_", ""), 10);
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: "", exchange_rate: "" };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      } else {
        // Input charge row in invoice
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== invoiceId) return inv;
            const updated = { ...inv.inputChargeRow, currency: "", exchange_rate: "" };
            const calc = calculateChargeValues(updated);
            return { ...inv, inputChargeRow: { ...updated, ...calc } };
          })
        );
      }
      return;
    }

    // INR = base currency
    if (country_code === "INR") {
      setEditedExchangeRates((prev) => ({ ...prev, INR: 1 }));
      if (chargeKey) {
        const index = parseInt(chargeKey.replace("charge_", ""), 10);
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: "INR", exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      } else {
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== invoiceId) return inv;
            const updated = { ...inv.inputChargeRow, currency: "INR", exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...inv, inputChargeRow: { ...updated, ...calc } };
          })
        );
      }
      return;
    }

    // Use cached rate if available
    if (editedExchangeRates[country_code] !== undefined) {
      const editedRate = Number(editedExchangeRates[country_code]);
      if (chargeKey) {
        const index = parseInt(chargeKey.replace("charge_", ""), 10);
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: editedRate };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      } else {
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== invoiceId) return inv;
            const updated = { ...inv.inputChargeRow, currency: country_code, exchange_rate: editedRate };
            const calc = calculateChargeValues(updated);
            return { ...inv, inputChargeRow: { ...updated, ...calc } };
          })
        );
      }
      return;
    }

    // Fetch exchange rate from API first
    try {
      const res = await api.get(`/exchange_rate/rate/${country_code}`);
      const exchangeRateFromApi =
        res.data?.rate_in_NRO || res.data?.rate_in_INR || 1;
      const exchangeRate = Number(exchangeRateFromApi);

      // Store in cache
      setEditedExchangeRates((prev) => ({
        ...prev,
        [country_code]: exchangeRate,
      }));

      // Update both currency and exchange_rate together with correct calculation
      if (chargeKey) {
        const index = parseInt(chargeKey.replace("charge_", ""), 10);
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: exchangeRate };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      } else {
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== invoiceId) return inv;
            const updated = { ...inv.inputChargeRow, currency: country_code, exchange_rate: exchangeRate };
            const calc = calculateChargeValues(updated);
            return { ...inv, inputChargeRow: { ...updated, ...calc } };
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);

      // Fallback: set currency with exchange_rate = 1
      if (chargeKey) {
        const index = parseInt(chargeKey.replace("charge_", ""), 10);
        setChargeRow((prev) =>
          prev.map((row, i) => {
            if (i !== index) return row;
            const updated = { ...row, currency: country_code, exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...updated, ...calc };
          })
        );
      } else {
        setInvoices((prev) =>
          prev.map((inv) => {
            if (inv.id !== invoiceId) return inv;
            const updated = { ...inv.inputChargeRow, currency: country_code, exchange_rate: 1 };
            const calc = calculateChargeValues(updated);
            return { ...inv, inputChargeRow: { ...updated, ...calc } };
          })
        );
      }
    }
  };

  const ensureExchangeRate = async (currency) => {
    if (!currency) return;

    if (currency === "INR") {
      setEditedExchangeRates((prev) => ({ ...prev, INR: 1 }));
      return;
    }

    if (editedExchangeRates[currency] !== undefined) return;

    try {
      const res = await api.get(`/exchange_rate/rate/${currency}`);
      const exchangeRateFromApi =
        res.data?.rate_in_NRO || res.data?.rate_in_INR || 1;
      const exchangeRate = Number(exchangeRateFromApi);
      setEditedExchangeRates((prev) => ({ ...prev, [currency]: exchangeRate }));
    } catch (err) {
      console.error("Failed to fetch invoice exchange rate:", err);
    }
  };

  const handleInputChargeChange = (field, value) => {
    setInputChargeRow((prev) => {
      const updated = { ...prev, [field]: value };

      // If charge code changes, reset GST-related fields then fetch/restore GST
      if (field === "charge_code") {
        updated.sac_code = "";
        updated.gst_percentage = 0;
        updated.gst_amount = 0;

        // Try to get GST from cache for the new charge code
        const cachedGst = gstPercentageCache[value];
        updated.default_gst_percentage =
          cachedGst !== undefined ? cachedGst : 0;

        // If GST was already set to "yes" before charge code change, apply the cached GST immediately
        if (prev.gst_option?.value === "yes") {
          updated.gst_percentage = cachedGst !== undefined ? cachedGst : 0;
        }

        // Trigger fetch if not already cached
        if (cachedGst === undefined) {
          fetchGSTForChargeCode(value, "input");
        }
      }

      // If GST option toggled, set GST percentage accordingly
      if (field === "gst_option") {
        if (value?.value === "yes") {
          // Check cache first, then default_gst_percentage
          const cachedGst = gstPercentageCache[updated.charge_code];
          const gstPct =
            cachedGst !== undefined
              ? cachedGst
              : updated.default_gst_percentage || 0;
          updated.gst_percentage = gstPct;
        } else {
          updated.gst_percentage = 0;
        }
      }

      // If currency changes to non-INR, disable GST
      if (field === "currency" && value !== "INR") {
        updated.gst_option = null;
        updated.gst_percentage = 0;
        updated.gst_amount = 0;
      }

      // Recalculate amount, amount_in_inr, gst_amount
      const calc = calculateChargeValues(updated);
      return { ...updated, ...calc };
    });
  };

  const addChargeRow = () => {
    if (
      !inputChargeRow.charge_code ||
      !inputChargeRow.currency ||
      !inputChargeRow.rate ||
      !inputChargeRow.quantity
    ) {
      setErrors((prev) => ({
        ...prev,
        chargeTable:
          "Please fill all required fields in the input row before adding a new row",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, chargeTable: "" }));

    const finalGstPercentage =
      inputChargeRow.currency === "INR" && inputChargeRow.gst_option?.value === "yes"
        ? gstPercentageCache[inputChargeRow.charge_code] !== undefined
          ? gstPercentageCache[inputChargeRow.charge_code]
          : inputChargeRow.gst_percentage || 0
        : 0;

    const newCharge = {
      ...inputChargeRow,
      gst_percentage: finalGstPercentage,
    };

    // Recalculate GST amount if GST enabled
    if (inputChargeRow.currency === "INR" && inputChargeRow.gst_option?.value === "yes" && finalGstPercentage > 0) {
      newCharge.gst_amount = Number(
        ((newCharge.amount_in_inr * finalGstPercentage) / 100).toFixed(2),
      );
    }

    setChargeRow((prev) => {
      const updated = [...prev, newCharge];
      return updated;
    });

    setInputChargeRow({
      charge_code: "",
      sac_code: "",
      default_gst_percentage: inputChargeRow.default_gst_percentage,
      currency: "",
      exchange_rate: 1,
      rate: 0,
      quantity: 0,
      amount: 0,
      amount_in_inr: 0,
      gst_option: null,
      gst_percentage: inputChargeRow.default_gst_percentage || 0,
      gst_amount: 0,
    });
  };

  const handleChargeInputChange = (index, field, value) => {
    setChargeRow((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      if (field === "charge_code") {
        const chargeCode = value;

        updated[index].sac_code = "";
        updated[index].default_gst_percentage = 0;
        updated[index].gst_percentage = 0;
        updated[index].gst_amount = 0;

        // Use cached GST if available
        const cachedGst = gstPercentageCache[chargeCode];

        if (cachedGst !== undefined) {
          updated[index].default_gst_percentage = cachedGst;

          if (updated[index].gst_option?.value === "yes") {
            updated[index].gst_percentage = cachedGst;
          }
        }

        // Fetch SAC + GST
        if (chargeCode && cachedGst === undefined) {
          fetchGSTForChargeCode(chargeCode, index);
        }
      }

      if (field === "gst_option") {
        const chargeCode = updated[index].charge_code;

        if (value?.value === "yes") {
          const cachedGst = gstPercentageCache[chargeCode];

          if (cachedGst !== undefined) {
            updated[index].gst_percentage = cachedGst;
          } else {
            updated[index].gst_percentage = 0;

            fetchGSTForChargeCode(chargeCode, index);
          }
        } else {
          // GST NO
          updated[index].gst_percentage = 0;
          updated[index].gst_amount = 0;
        }
      }

      if (field === "currency" && value !== "INR") {
        updated[index].gst_option = null;
        updated[index].gst_percentage = 0;
        updated[index].gst_amount = 0;
      }

      // Recalculate amount, amount_in_inr, gst_amount
      const calc = calculateChargeValues(updated[index]);
      updated[index] = { ...updated[index], ...calc };

      return updated;
    });
  };

  const deleteChargeRow = (index) => {
    setChargeRow((prev) => {
      const updatedCharges = prev.filter((_, i) => i !== index);
      return updatedCharges;
    });
    // Reindex charge_keys across all invoices so they stay aligned with
    // the new chargeRow positions after deletion.
    setInvoices((prev) =>
      prev.map((inv) => ({
        ...inv,
        charge_keys: (inv.charge_keys || [])
          .filter((_, i) => i !== index)
          .map((_, i) => `charge_${i}`),
      })),
    );
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const fetchPorts = async () => {
    try {
      setLoadingPorts(true);
      const res = await api("/ports");
      const options = res.data.map((p) => ({
        value: p.Port_id,
        label: p.Port_names,
      }));
      setPorts(options);
    } catch (err) {
      console.error("Error fetching ports:", err);
    } finally {
      setLoadingPorts(false);
    }
  };

  const fetchEnquiryData = async (enqId) => {
    try {
      setLoadingData(true);
      const res = await api(`/enquiry/viewEnquiry/${enqId}`);
      const data = res.data;
      const enquiry = data;
      if (enquiry) {
        const isFCL = ["FE", "FI"].includes(enquiry.shipment_segment);
        const isLCL = ["LE", "LI"].includes(enquiry.shipment_segment);
        const isAir = ["AE", "AI"].includes(enquiry.shipment_segment);
        const isExport = ["FE", "LE", "AE"].includes(enquiry.shipment_segment);

        // CBM lives in container_details, not on the enquiry row.
        const totalCBM = (enquiry.containerDetails || []).reduce(
          (sum, c) => sum + (Number(c.cbm) || 0),
          0,
        );

        setFormData((prev) => ({
          ...prev,
          job_number: enquiry.job_number || "",
          // Blank on create, but must not wipe the saved value while editing:
          // this effect makes two sequential requests and therefore resolves
          // after loadInvoiceForEdit, which would blank the billing party that
          // was just loaded from sale_invoices.
          billing_party: editSaleInvoiceId ? prev.billing_party : "",
          shipper: enquiry.first_shipper_name || "",
          shipper_address: enquiry.first_shipper_address || "",
          consignee: enquiry.first_consignee_name || "",
          consignee_address: enquiry.first_consignee_address || "",
          notify_party1: enquiry.notify_party_1 || enquiry.notify_party1 || "",
          notify_party1_address: enquiry.notify_party_1_address || "",
          notify_party2: enquiry.notify_party_2 || enquiry.notify_party2 || "",
          notify_party2_address: enquiry.notify_party_2_address || "",
          pol: enquiry.pol || "",
          pod: enquiry.pod || "",
          place_of_receipt: enquiry.place_of_receipt || "",
          final_port_of_delivery: enquiry.final_port_of_delivery || "",
          transshipment_hub: enquiry.transshipment_hub || "",
          shipment_segment: enquiry.shipment_segment || "",
          incoterms: enquiry.incoterm || "",
          commodity_name: enquiry.hs_description || "",
          freight_terms: normalizeFreightTerms(enquiry.freight_terms),
          gross_weight: enquiry.gross_weight || "",
          number_of_packages: enquiry.packaging_count || "",
          credit_days: enquiry.credit_days || "",
          credit_days_system: enquiry.credit_days_system || "",
          cbm: totalCBM ? String(totalCBM) : "",
          chargeable_weight: enquiry.chargeable_weight || "",
          container_number: isFCL ? enquiry.container_number || "" : "",
          airline_name: isAir ? enquiry.airline_name || "" : "",
          consolidation_at: isLCL ? enquiry.stuffing_destuffing_At || "" : "",
          vessel_name: enquiry.vessel_name || "",
          voyage_number: enquiry.voyage_number || "",
          routing: enquiry.routing || formData.routing,

          bl_number: enquiry.BL_id || "",
          other_reference: enquiry.commercial_invoice_number || "",
        }));
        setContainerTableData(
          res.data.containerDetails?.map((c) => ({
            // keep container_id so the server can UPDATE instead of INSERT,
            // preserving the purchase_charge_containers FK
            container_id: c.container_id,
            container_type: c.container_type,
            container_number: c.container_number || "",
            seal_number: c.seal_number || "",
            container_weight: c.container_weight,
            container_length: c.container_length,
            container_breadth: c.container_breadth,
            container_height: c.container_height,
            container_temperature: c.container_temperature,
          })) || [],
        );
      }

      // Fetch quotation charges for this enquiry
      // Skip during edit mode: loadInvoiceForEdit sets chargeRow from backend.
      // If we overwrite here, we'd show the wrong invoice's charges due to
      // race condition between the two parallel effects.
      if (!editSaleInvoiceId) {
        try {
          const qRes = await api.get(`/quotation/viewQuotation/${enqId}`);
          const qData = qRes.data;
          const qCharges = qData?.quotation_charges || [];
          if (qCharges.length > 0) {
            const mapped = qCharges.map((qc) => {
              const rate = Number(qc.amount || 0);
              const quantity = Number(qc.quantity || 0);
              const exchangeRate = Number(qc.exchange_rate || 1);
              const amount = rate * quantity;
              const amountInInr =
                qc.charge_currency === "INR" ? amount : amount * exchangeRate;
              const gstPercentage = Number(qc.gst_percentage || 0);

              const gstAmount =
                gstPercentage > 0 ? (amountInInr * gstPercentage) / 100 : 0;

              return {
                charge_code: qc.charge_code || "",
                sac_code: qc.sac_code || "",
                currency: qc.charge_currency || "",
                exchange_rate: exchangeRate,
                rate: rate,
                quantity: quantity,
                amount: Number(amount.toFixed(2)),
                amount_in_inr: Number(amountInInr.toFixed(2)),

                gst_option:
                  gstPercentage > 0
                    ? { value: "yes", label: "Yes" }
                    : { value: "no", label: "No" },

                gst_percentage: gstPercentage,
                gst_amount: Number(gstAmount.toFixed(2)),
              };
            });

            setChargeRow(mapped);
            setQuotationCharges(mapped);
          }
        } catch (qErr) {
          console.error("Failed to fetch quotation charges:", qErr);
        }
      }
    } catch (error) {
      console.error("❌ Error fetching enquiry data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  // Restore user-entered draft from sessionStorage (survives Back navigation)
  const restoreDraft = () => {
    try {
      // Never restore while editing. The draft key is per-enquiry, so every
      // invoice under one enquiry shares it, and the stored copy belongs to
      // whichever invoice was last saved. Layering it over the row loaded by
      // loadInvoiceForEdit would prefill a different invoice's values, and the
      // two run in separate effects so either can win the race.
      console.log("RESTORE DRAFT CALLED:", { editSaleInvoiceId, id });
      if (editSaleInvoiceId) {
        console.log("RESTORE DRAFT SKIPPED: edit mode active");
        return;
      }
      const savedDraft = sessionStorage.getItem(`sales_invoice_draft_${id}`);
      if (!savedDraft) return;
      const draft = JSON.parse(savedDraft);
      if (!draft) return;

      setFormData((prev) => ({
        ...prev,
        job_number: draft.job_number ?? prev.job_number,
        lut_number: draft.lut_number ?? prev.lut_number,
        inv_number: draft.inv_number ?? prev.inv_number,
        bl_number: draft.bl_number ?? prev.bl_number,
        other_reference: draft.other_reference ?? prev.other_reference,
        invoice_date: draft.invoice_date ?? prev.invoice_date,
        billing_party: draft.billing_party ?? prev.billing_party,
        shipper: draft.shipper ?? prev.shipper,
        shipper_address: draft.shipper_address ?? prev.shipper_address,
        consignee: draft.consignee ?? prev.consignee,
        consignee_address: draft.consignee_address ?? prev.consignee_address,
        notify_party1: draft.notify_party1 ?? prev.notify_party1,
        notify_party1_address: draft.notify_party1_address ?? prev.notify_party1_address,
        notify_party2: draft.notify_party2 ?? prev.notify_party2,
        notify_party2_address: draft.notify_party2_address ?? prev.notify_party2_address,
        pol: draft.pol ?? prev.pol,
        pod: draft.pod ?? prev.pod,
        place_of_receipt: draft.place_of_receipt ?? prev.place_of_receipt,
        final_port_of_delivery:
          draft.final_port_of_delivery ?? prev.final_port_of_delivery,
        transshipment_hub: draft.transshipment_hub ?? prev.transshipment_hub,
        shipment_segment: draft.shipment_segment ?? prev.shipment_segment,
        incoterms: draft.incoterms ?? prev.incoterms,
        commodity_name: draft.commodity_name ?? prev.commodity_name,
        freight_terms: draft.freight_terms
          ? normalizeFreightTerms(draft.freight_terms)
          : prev.freight_terms,
        gross_weight: draft.gross_weight ?? prev.gross_weight,
        number_of_packages: draft.number_of_packages ?? prev.number_of_packages,
        cbm: draft.cbm ?? prev.cbm,
        chargeable_weight: draft.chargeable_weight ?? prev.chargeable_weight,
        airline_name: draft.airline_name ?? prev.airline_name,
        consolidation_at: draft.consolidation_at ?? prev.consolidation_at,
        vessel_name: draft.vessel_name ?? prev.vessel_name,
        voyage_number: draft.voyage_number ?? prev.voyage_number,
        payment_ref: draft.payment_ref ?? prev.payment_ref,
        payment_date: draft.payment_date ?? prev.payment_date,
        term_and_condition: draft.term_and_condition ?? prev.term_and_condition,
      }));

      if (draft.invoice_date) {
        const parsedDate = new Date(draft.invoice_date);
        if (!Number.isNaN(parsedDate.getTime())) {
          setDateOfIssue(parsedDate);
        }
      }

      if (Array.isArray(draft.containers) && draft.containers.length > 0) {
        setContainerTableData(draft.containers);
      }

      if (Array.isArray(draft.charges) && draft.charges.length > 0) {
        setChargeRow(
          draft.charges.map((c) => ({
            charge_code: c.charge_code || "",
            sac_code: c.sac_code || "",
            currency: c.currency || "",
            exchange_rate: Number(c.exchange_rate) || 1,
            rate: Number(c.rate) || 0,
            quantity: Number(c.quantity) || 0,
            amount: Number(c.amount) || 0,
            amount_in_inr: Number(c.amount_in_inr) || 0,
            gst_option:
              c.gst_option === "yes" || c.gst_option === "no"
                ? {
                  value: c.gst_option,
                  label: c.gst_option === "yes" ? "Yes" : "No",
                }
                : c.gst_option || null,
            gst_percentage: Number(c.gst_percentage) || 0,
            gst_amount: Number(c.gst_amount) || 0,
          })),
        );
      }

      // Restore edited exchange rates
      if (draft.editedExchangeRates) {
        setEditedExchangeRates(draft.editedExchangeRates);
      }

      if (Array.isArray(draft.invoices) && draft.invoices.length > 0) {
        setInvoices(
          draft.invoices.map((inv) => ({
            id: inv.id,
            invoice_number: inv.invoice_number || "",
            invoice_date: inv.invoice_date || "",
            billing_party: inv.billing_party || "Shipper",
            currency: inv.currency || "INR",
            notify_party2: inv.notify_party2 || "",
            notify_party2_address: inv.notify_party2_address || "",
            charge_keys: Array.isArray(inv.charge_keys) ? [...inv.charge_keys] : [],
            inputChargeRow: {
              charge_code: "",
              sac_code: "",
              default_gst_percentage: 0,
              currency: "",
              exchange_rate: 1,
              rate: 0,
              quantity: 0,
              amount: 0,
              amount_in_inr: 0,
              gst_option: null,
              gst_percentage: 0,
              gst_amount: 0,
            },
          })),
        );
      }
    } catch (err) {
      console.error("Failed to restore invoice draft:", err);
    }
  };





  useEffect(() => {
    const fetchChargeCodes = async () => {
      try {
        setLoadingChargeCodes(true);
        const response = await api.get("/charge_code/options");
        // console.log("response.data", response.data);

        setChargeCodeOptions(response.data);
      } catch (err) {
        console.error("Failed to fetch charge codes:", err);
      } finally {
        setLoadingChargeCodes(false);
      }
    };

    fetchChargeCodes();
  }, []);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await api.get("/exchange_rate");

        const formatted = response.data.map((item) => ({
          value: item.country_code,
          label: item.country_code,
        }));

        setCurrencyOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchExchangeRates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handlePolChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      pol: opt?.value || "",
    }));
  };

  const handlePodChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      pod: opt?.value || "",
    }));
  };

  const validate = () => {
    const err = {};

    // Required fields
    if (!formData.job_number) err.job_number = "Job Number is required";
    if (!formData.other_reference)
      err.other_reference = "Other Reference is Required";
    if (!formData.notify_party1)
      err.notify_party1 = "Notify Party 1 is Required";
    if (!formData.shipper) err.shipper = "Shipper is required";
    if (!formData.consignee) err.consignee = "Consignee is required";
    if (!formData.pol) err.pol = "Port of Loading is required";
    if (!formData.pod) err.pod = "Port of Discharge is required";
    if (!formData.credit_days)
      err.credit_days = "Credit Days Actual is Required";
    if (!formData.credit_days_system)
      err.credit_days_system = "Credit Days (As per System) is Required";
    if (!formData.shipment_segment)
      err.shipment_segment = "Shipment Segment is required";
    if (!formData.commodity_name)
      err.commodity_name = "Commodity Name is required";
    if (!formData.freight_terms)
      err.freight_terms = "Freight Terms is required";
    if (!formData.vessel_name) err.vessel_name = "Vessel Name is required";
    if (!formData.term_and_condition)
      err.term_and_condition = "Term & Conditions is required";
    if (!formData.routing) err.routing = "Routing is required";
    if (
      ["Indirect", "Both"].includes(formData.routing) &&
      !formData.transshipment_hub
    )
      err.transshipment_hub = "Transhipment Hub is required";
    if (!formData.final_port_of_delivery)
      err.final_port_of_delivery = "Final Port of Delivery is Required";
    if (!formData.gross_weight) err.gross_weight = "Gross Weight is Required";
    if (!formData.number_of_packages)
      err.number_of_packages = "Number Of Packages is Required";
    if (!formData.vessel_name) err.vessel_name = "Vessel Name is Required";
    if (!formData.voyage_number)
      err.voyage_number = "Voyage Number is Required";

    // Conditional validations
    if (
      ["AE", "AI"].includes(formData.shipment_segment) &&
      !formData.airline_name
    ) {
      err.airline_name = "Airline Name is required for air shipments";
    }
    if (
      ["LE", "LI"].includes(formData.shipment_segment) &&
      !formData.consolidation_at
    ) {
      err.consolidation_at =
        formData.shipment_segment === "LI"
          ? "Destuffing At is required for LCL import shipments"
          : "Stuffing At is required for LCL export shipments";
    }

    // Charges validation
    if (chargeRow.length === 0) {
      err.chargeTable = "At least one charge is required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submitInvoice = async () => {
    setLoadingSubmit(true);
    try {
      // Shared fields (shipper, consignee, ports, vessel, etc.) live in enquiry table.
      // Only invoice-specific fields are sent to sale_invoices.
      const firstInvoice = invoices[0];
      const data = {
        enq_id: id,
        job_number: formData.job_number,
        lut_number: formData.lut_number,
        inv_number: firstInvoice?.invoice_number || "",
        invoice_date: firstInvoice?.invoice_date || "",
        billing_party: firstInvoice?.billing_party || "Shipper",
        term_and_condition: formData.term_and_condition,
        sub_total: totals.subTotal,
        gst_total: totals.gstAmount,
        grand_total: totals.grandTotal,
        invoice_type: invoiceType, // "performa" or "final"
        charges: chargeRow.map((c) => ({
          charge_code: c.charge_code,
          sac_code: c.sac_code,
          currency: c.currency,
          exchange_rate: c.exchange_rate,
          rate: c.rate,
          quantity: c.quantity,
          amount: c.amount,
          amount_in_inr: c.amount_in_inr,
          amount_in_currency: convertInrToInvoiceCurrency(
            c.amount_in_inr,
            firstInvoice?.currency || "INR"
          ),
          gst_option: c.gst_option?.value || "no",
          gst_percentage: c.gst_percentage,
          gst_amount: c.gst_amount,
        })),
      };

      // Use different endpoints based on invoice type
      const endpoint =
        invoiceType === "performa"
          ? "/sale-invoices/createPerforma"
          : "/sale-invoices/create";

      const res = await api.post(endpoint, data);
      if (res.data.success) {
        setShowConfirm(false);
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Failed to create invoice:", err);
      setError("Failed to create invoice. Please try again.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleSubmit = (e, type = "performa", openPerformaPage = false) => {
    e.preventDefault();

    // Guard the Enter-key path: form onSubmit must never create a Final
    // invoice, since those come from the Tally integration.
    if (type === "final") return;

    if (!validate()) return;
    setInvoiceType(type);
    setShowConfirm(true);

    if (openPerformaPage) {
      navigate(
        id && id !== "new"
          ? `/create-performa-invoice?enq_id=${id}`
          : "/create-performa-invoice",
      );
    }
  };

  const draftStorageKey = `sales_invoice_draft_${id}`;

  // Save current form state to sessionStorage so the preview page can read it
  const saveDraftForPreview = () => {
    const firstInvoice = invoices[0];
    // Single invoice edit: compute totals and charges from assigned charges
    // only (via charge_keys), not from all of chargeRow. chargeRow may still
    // contain deleted charges whose keys were removed from charge_keys.
    const assigned = getInvoiceCharges(firstInvoice);
    const subTotal = assigned.reduce(
      (acc, { charge }) => acc + Number(charge.amount_in_inr || 0),
      0,
    );
    const gstAmount = assigned.reduce((acc, { charge }) => {
      return (
        acc +
        (charge.gst_option?.value === "yes" ? Number(charge.gst_amount || 0) : 0)
      );
    }, 0);
    const draft = {
      enq_id: id,
      invoice_type: "performa",
      // null on create; the existing sale_invoice_id when editing. The
      // preview page uses this to choose POST vs PUT.
      sale_invoice_id: editSaleInvoiceId || null,
      job_number: formData.job_number,
      lut_number: formData.lut_number,
      bl_number: formData.bl_number,
      other_reference: formData.other_reference,
      term_and_condition: formData.term_and_condition,
      shipper: formData.shipper,
      shipper_address: formData.shipper_address,
      consignee: formData.consignee,
      consignee_address: formData.consignee_address,
      notify_party: formData.notify_party1 || formData.notify_party2,
      notify_party1: formData.notify_party1,
      notify_party1_address: formData.notify_party1_address,
      notify_party2: formData.notify_party2,
      notify_party2_address: formData.notify_party2_address,
      pol: formData.pol,
      pod: formData.pod,
      place_of_receipt: formData.place_of_receipt,
      final_port_of_delivery: formData.final_port_of_delivery,
      transshipment_hub: formData.transshipment_hub,
      shipment_segment: formData.shipment_segment,
      incoterms: formData.incoterms,
      commodity_name: formData.commodity_name,
      description_of_goods: formData.description_of_goods,
      freight_terms: formData.freight_terms,
      gross_weight: formData.gross_weight,
      cbm: formData.cbm,
      chargeable_weight: formData.chargeable_weight,
      number_of_packages: formData.number_of_packages,
      credit_days: formData.credit_days,
      credit_days_system: formData.credit_days_system,
      containers: containerTableData,
      airline_name: formData.airline_name,
      consolidation_at: formData.consolidation_at,
      vessel_name: formData.vessel_name,
      voyage_number: formData.voyage_number,
      routing: formData.routing,
      payment_ref: formData.payment_ref,
      payment_date: formData.payment_date,
      // Collect split. The preview page renders each invoice from this array.
      invoices: invoices.map((inv) => ({
        id: inv.id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date,
        billing_party: inv.billing_party,
        currency: inv.currency,
        notify_party2: inv.notify_party2,
        notify_party2_address: inv.notify_party2_address,
        charge_keys: [...(inv.charge_keys || [])],
      })),
      sub_total: Number(subTotal.toFixed(2)),
      gst_total: Number(gstAmount.toFixed(2)),
      cgst_total: Number((gstAmount / 2).toFixed(2)),
      sgst_total: Number((gstAmount / 2).toFixed(2)),
      grand_total: Number((subTotal + gstAmount).toFixed(2)),
      shipper_name: formData.shipper,
      consignee_name: formData.consignee,
      notify_party_name: formData.notify_party1 || formData.notify_party2,
      editedExchangeRates,
      charges: assigned.map(({ charge, key }) => ({
        // Keep the original row_key so CreatePerformaInvoice.js can look up
        // this charge by the same key stored in invoices[].charge_keys.
        row_key: key,
        charge_code:
          typeof charge.charge_code === "object"
            ? charge.charge_code?.value || ""
            : charge.charge_code || "",
        sac_code: charge.sac_code,
        currency: charge.currency,
        exchange_rate: charge.exchange_rate,
        rate: charge.rate,
        quantity: charge.quantity,
        amount: charge.amount,
        amount_in_inr: charge.amount_in_inr,
        amount_in_invoice_currency: charge.currency !== "INR" && charge.exchange_rate > 0
          ? Number((charge.amount_in_inr / charge.exchange_rate).toFixed(2))
          : charge.amount_in_inr,
        gst_option: charge.gst_option?.value || "no",
        gst_percentage: charge.gst_percentage,
        gst_amount: charge.gst_amount,
      })),
    };
    try {
      sessionStorage.setItem(draftStorageKey, JSON.stringify(draft));
    } catch (e) {
      console.error("Failed to save draft for preview:", e);
    }
  };

  if (loadingData || loadingPorts) {
    return (
      <>
        <BackButton />
        <div className="text-center py-5">Loading...</div>
      </>
    );
  }

  return (
    <>
      <BackButton />

      <form onSubmit={handleSubmit}>
        {/* {Object.entries(errors).filter(([, msg]) => typeof msg === "string" && msg).length > 0 && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>Please fix the following errors before continuing:</strong>
            <ul className="mb-0 mt-2 ps-3">
              {Object.entries(errors)
                .filter(([, msg]) => typeof msg === "string" && msg)
                .map(([key, msg]) => (
                  <li key={key}>{msg}</li>
                ))}
            </ul>
          </div>
        )} */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="d-flex align-items-center mb-1">
              <label className="form-label mb-0">Job Number</label>
              <Tooltip title="Job Number is auto-assigned to a booking once it is confirmed.">
                <InfoIcon
                  title=""
                  sx={{
                    fontSize: "1rem",
                    cursor: "default",
                    color: "#6c757d",
                    ml: "4px",
                  }}
                />
              </Tooltip>
            </div>
            <InputField
              name="job_number"
              value={formData.job_number}
              onChange={handleChange}
              disabled
            />
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center mb-1">
              <label className="form-label mb-0">HBL Number</label>
              <Tooltip title="HBL Number is autogenerated by the system once the BL Draft or Final BL is created.">
                <InfoIcon
                  title=""
                  sx={{
                    fontSize: "1rem",
                    cursor: "default",
                    color: "#6c757d",
                    ml: "4px",
                  }}
                />
              </Tooltip>
            </div>
            <InputField
              name="bl_number"
              value={formData.bl_number}
              readOnly
              error={errors.bl_number}
              placeholder="Autogenerated on BL Draft / Final BL creation"
              styleInput={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
              }}
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Commercial Invoice Number"
              name="other_reference"
              value={formData.other_reference}
              onChange={handleChange}
              error={errors.other_reference}
              placeholder="Enter Other Reference / Commercial Invoice Number"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Shipper"
              name="shipper"
              value={formData.shipper}
              onChange={handleChange}
              error={errors.shipper}
              placeholder="Enter Shipper"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Consignee"
              name="consignee"
              value={formData.consignee}
              onChange={handleChange}
              error={errors.consignee}
              placeholder="Enter Consignee"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Shipper Address"
              name="shipper_address"
              value={formData.shipper_address}
              onChange={handleChange}
              placeholder="Enter Shipper Address"
            />
          </div>


          <div className="col-md-4">
            <InputField
              label="Consignee Address"
              name="consignee_address"
              value={formData.consignee_address}
              onChange={handleChange}
              placeholder="Enter Consignee Address"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Notify Party1"
              name="notify_party1"
              value={formData.notify_party1}
              onChange={handleChange}
              error={errors.notify_party1}
              placeholder="Enter Notify Party1"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Notify Party1 Address"
              name="notify_party1_address"
              value={formData.notify_party1_address}
              onChange={handleChange}
              placeholder="Enter Notify Party1 Address"
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Port of Loading</label>
            <Select
              options={portsOptions}
              value={
                portsOptions.find((p) => p.value === formData.pol) ||
                (formData.pol
                  ? { value: formData.pol, label: formData.pol }
                  : null)
              }
              onChange={handlePolChange}
              placeholder="Select Port of Loading"
              isClearable
              className={errors.pol ? "is-invalid" : ""}
            />
            {errors.pol && <div className="text-danger mt-1">{errors.pol}</div>}
          </div>
          <div className="col-md-4">
            <label className="form-label">Port of Discharge</label>
            <Select
              options={portsOptions}
              value={
                portsOptions.find((p) => p.value === formData.pod) ||
                (formData.pod
                  ? { value: formData.pod, label: formData.pod }
                  : null)
              }
              onChange={handlePodChange}
              placeholder="Select Port of Discharge"
              isClearable
              className={errors.pod ? "is-invalid" : ""}
            />
            {errors.pod && <div className="text-danger mt-1">{errors.pod}</div>}
          </div>
          <div className="col-md-4">
            <InputField
              label="Place of Receipt"
              name="place_of_receipt"
              value={formData.place_of_receipt}
              error={errors.place_of_receipt}
              onChange={handleChange}
              placeholder="Enter Place of Receipt"
            />
          </div>

          <div className="col-sm-6 col-md-4">
            <InputField
              label="Final Port Of Delivery"
              name="final_port_of_delivery"
              placeholder="Enter Final Port Of Delivery"
              error={errors.final_port_of_delivery}
              value={formData.final_port_of_delivery}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Shipment Segment"
              name="shipment_segment"
              value={formData.shipment_segment}
              onChange={handleChange}
              error={errors.shipment_segment}
              placeholder="Enter Shipment Segment"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Incoterms"
              name="incoterms"
              value={formData.incoterms}
              onChange={handleChange}
              placeholder="Enter Incoterms"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Commodity Name"
              name="commodity_name"
              value={formData.commodity_name}
              onChange={handleChange}
              error={errors.commodity_name}
              placeholder="Enter Commodity Name"
            />
          </div>
          <div className="col-md-4">
            <SelectField
              label="Freight Terms"
              name="freight_terms"
              options={freight_terms_options}
              value={formData.freight_terms}
              onChange={(val) => handleFormChange("freight_terms", val)}
              error={errors.freight_terms}
              placeholder="Select Freight Terms"
            />
          </div>
          {["FE", "FI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Gross Weight"
                name="gross_weight"
                value={formData.gross_weight}
                error={errors.gross_weight}
                onChange={handleChange}
                placeholder="Enter Gross Weight"
              />
            </div>
          )}

          <div className="col-md-4">
            <InputField
              label="Number of Packages"
              name="number_of_packages"
              value={formData.number_of_packages || formData.packaging}
              onChange={handleChange}
              error={errors.number_of_packages}
              placeholder="Enter Number of Packages"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Credit Days Actual"
              name="credit_days"
              value={formData.credit_days}
              onChange={handleChange}
              error={errors.credit_days}
              placeholder="Enter Credit Days Actual"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Credit Days (As per System)"
              name="credit_days_system"
              value={formData.credit_days_system}
              onChange={handleChange}
              error={errors.credit_days_system}
              placeholder="Enter Credit Days(As per System) "
            />
          </div>
          {/* Routing */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Routing"
              options={routing_options}
              // clsname="required"
              value={formData.routing}
              onChange={(val) => handleFormChange("routing", val)}
              error={errors.routing}
            />
          </div>
          {["Indirect", "Both"].includes(formData?.routing) && (
            <div className="col-md-4">
              <label className="form-label">Transshipment Hub</label>

              <Select
                options={portsOptions}
                value={
                  portsOptions?.find(
                    (p) =>
                      p.value === formData?.transshipment_hub ||
                      p.label === formData?.transshipment_hub,
                  ) || null
                }
                onChange={(val) =>
                  handleChange({
                    target: { name: "transshipment_hub", value: val?.label || "" },
                  })
                }
                placeholder="Select Transshipment Hub"
                isSearchable
                name="transshipment_hub"
              />
            </div>
          )}

          {["LE", "LI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="CBM"
                name="cbm"
                value={formData.cbm}
                onChange={handleChange}
                placeholder="Enter CBM"
              />
            </div>
          )}
          {["AE", "AI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Chargeable Weight"
                name="chargeable_weight"
                value={formData.chargeable_weight}
                onChange={handleChange}
                placeholder="Enter Chargeable Weight"
              />
            </div>
          )}
          {["FE", "FI", "LE", "LI"].includes(formData.shipment_segment) &&
            containerTableData.length > 0 && (
              <div className="col-12 mb-3">
                <h5 className="fs-6 fw-bold">Container Details</h5>
                <table
                  className="table table-bordered"
                  style={{ marginTop: "10px" }}
                >
                  <thead>
                    <tr>
                      {formData.shipment_segment !== "LE" &&
                        formData.shipment_segment !== "LI" && (
                          <th>Container Type</th>
                        )}
                      <th>Weight</th>
                      <th>Length</th>
                      <th>Height</th>
                      <th>Breadth</th>
                      {formData.nature_of_cargo === "Temperature Control" && (
                        <th>Temperature</th>
                      )}
                      <th>Container Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {containerTableData.map((container, index) => (
                      <tr key={index}>
                        {formData.shipment_segment !== "LE" &&
                          formData.shipment_segment !== "LI" && (
                            <td>{container.container_type}</td>
                          )}
                        <td>{container.container_weight}</td>
                        <td>{container.container_length}</td>
                        <td>{container.container_height}</td>
                        <td>{container.container_breadth}</td>
                        {formData.nature_of_cargo === "Temperature Control" && (
                          <td>{container.container_temperature}</td>
                        )}
                        <td>
                          <InputField
                            placeholder="Enter Container Number"
                            value={container.container_number || ""}
                            onChange={(e) =>
                              handleContainerChange(
                                index,
                                "container_number",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {["AE", "AI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Airline Name"
                name="airline_name"
                value={formData.airline_name}
                onChange={handleChange}
                error={errors.airline_name}
                placeholder="Enter Airline Name"
              />
            </div>
          )}
          {["LE", "LI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label={
                  formData.shipment_segment === "LI"
                    ? "Destuffing At"
                    : "Stuffing At"
                }
                name="consolidation_at"
                value={formData.consolidation_at}
                onChange={handleChange}
                error={errors.consolidation_at}
                placeholder={`Enter ${formData.shipment_segment === "LI" ? "Destuffing At" : "Stuffing At"}`}
              />
            </div>
          )}
          <div className="col-md-4">
            <InputField
              label="Vessel Name"
              name="vessel_name"
              value={formData.vessel_name}
              onChange={handleChange}
              error={errors.vessel_name}
              placeholder="Enter Vessel Name"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Voyage Number"
              name="voyage_number"
              value={formData.voyage_number}
              onChange={handleChange}
              error={errors.voyage_number}
              placeholder="Enter Voyage Number"
            />
          </div>

          <div className="col-md-4">
            <InputField
              label="Payment Reference Number"
              name="payment_ref"
              value={formData.payment_ref}
              onChange={handleChange}
              error={errors.payment_ref}
              placeholder="Enter Payment Reference Number"
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Payment Date</label>

            <div className="position-relative">
              <DatePicker
                selected={
                  formData.payment_date ? new Date(formData.payment_date) : null
                }
                onChange={(date) => {
                  handleChange({
                    target: {
                      name: "payment_date",
                      value: date ? date.toISOString().split("T")[0] : "",
                    },
                  });
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YY"
                error={errors.payment_date}
                className="form-control pe-5"
                showPopperArrow={false}
              />

              <CalendarMonthIcon
                className="position-absolute"
                style={{
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        </div>

        {/* Invoice Sections — Prepaid, Collect, All Collect */}
        {needsInvoiceSections(formData.freight_terms) && (
          <div className="invoice-split-section mt-4">
            {errors.invoiceSplit && (
              <div className="text-danger mb-2">{errors.invoiceSplit}</div>
            )}
            {isSingleInvoiceEdit && (
              <div className="alert alert-warning mb-3">
                <strong>Editing Invoice {editInvoiceSectionIdx + 1} (Single Invoice Edit Mode)</strong>
                <br />
                You are editing only one invoice from a Collect split. Common fields (parties, ports, vessel, etc.) are shared across all invoices for this enquiry.
                Changes to common fields will affect all invoices when they are next regenerated.
              </div>
            )}
            {invoices.length === 0 ? (
              <div className="alert alert-info">
                Freight terms is <strong>Collect</strong>, so two invoices will be created. Click <strong>Add Invoice</strong> to create the first invoice section.
              </div>
            ) : (
              <div className="d-flex flex-column gap-4">
                {invoices.map((invoice, idx) => {
                  const invCharges = getInvoiceCharges(invoice);
                  const invTotals = computeInvoiceTotals(invCharges, invoice.currency);
                  const invTotalsInrSubTotal = invCharges.reduce(
                    (acc, { charge }) => acc + Number(charge.amount_in_inr || 0),
                    0
                  );
                  return (
                    <div key={invoice.id} className="border rounded p-3" style={{ background: "#f8f9fa" }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold">Invoice {idx + 1}</h6>
                        {!isSingleInvoiceEdit && (
                          <Button
                            variant="contained"
                            btncls="bg-danger text-white px-2 py-1"
                            size="small"
                            onClick={() => removeInvoice(invoice.id)}
                            disabled={invoices.length <= 1}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="row g-2 mb-3">
                        <div className="col-md-3">
                          <label className="form-label">Invoice Number</label>
                          <InputField
                            value={invoice.invoice_number || ""}
                            onChange={(e) => updateInvoice(invoice.id, "invoice_number", e.target.value)}
                            placeholder="Invoice Number"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Invoice Date</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={invoice.invoice_date ? new Date(invoice.invoice_date) : null}
                              onChange={(date) => {
                                updateInvoice(
                                  invoice.id,
                                  "invoice_date",
                                  date ? date.toISOString().split("T")[0] : "",
                                );
                              }}
                              dateFormat="dd/MM/yyyy"
                              placeholderText="DD/MM/YY"
                              className="form-control pe-5"
                              showPopperArrow={false}
                            />
                            <CalendarMonthIcon
                              className="position-absolute"
                              style={{
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#6c757d",
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Billing Party</label>
                          <select
                            className="form-control custom-select-text"
                            value={invoice.billing_party}
                            onChange={(e) => updateInvoice(invoice.id, "billing_party", e.target.value)}
                          >
                            {["Shipper", "Consignee", "Notify Party", "Notify Party 2"].map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Currency</label>
                          <select
                            className="form-control custom-select-text"
                            value={invoice.currency}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateInvoice(invoice.id, "currency", value);
                              ensureExchangeRate(value);
                            }}
                          >
                            {["INR", "USD", "EUR", "GBP"].map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>


                        </div>
                      </div>
                      {invoice.billing_party === "Notify Party 2" && (
                        <div className="row g-2 mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Notify Party 2</label>
                            <InputField
                              value={invoice.notify_party2 || ""}
                              onChange={(e) => updateInvoice(invoice.id, "notify_party2", e.target.value)}
                              placeholder="Enter Notify Party 2"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Notify Party 2 Address</label>
                            <InputField
                              value={invoice.notify_party2_address || ""}
                              onChange={(e) => updateInvoice(invoice.id, "notify_party2_address", e.target.value)}
                              placeholder="Enter Notify Party 2 Address"
                            />
                          </div>
                        </div>
                      )}
                      <div className="mb-3">
                        <Button
                          variant="outlined"
                          btncls="px-3"
                          size="small"
                          onClick={() => openChargeSelectModal(invoice.id)}
                        >
                          Select Charges from Quotation
                        </Button>
                      </div>
                      <div className="mb-4 position-relative">
                        <table className="table table-bordered tbllbl mb-2 mb-md-3">
                          <thead className="table-light">
                            <tr>
                              <th>Charge Code</th>
                              <th>SAC</th>
                              <th>Currency</th>
                              <th>Rate</th>
                              <th>Quantity</th>
                              <th>Amount</th>
                              <th>Amount in INR</th>
                              {invoice.currency !== "INR" && <th>{`Amount (${invoice.currency})`}</th>}
                              {invoice.currency === "INR" && <th>GST</th>}
                              {invoice.currency === "INR" && <th>GST %</th>}
                              {invoice.currency === "INR" && <th>GST Amount</th>}
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <SelectField
                                  options={chargeCodeOptions}
                                  value={invoice.inputChargeRow.charge_code}
                                  onChange={(val) => {
                                    updateInvoiceInputCharge(invoice.id, "charge_code", val);
                                    fetchGSTForChargeCode(val?.value, invoice.id, "invoice");
                                  }}
                                  isLoading={loadingChargeCodes}
                                  searchable
                                />
                              </td>
                              <td>
                                <InputField
                                  type="text"
                                  placeholder="SAC Code"
                                  value={invoice.inputChargeRow.sac_code || ""}
                                  onChange={(e) => updateInvoiceInputCharge(invoice.id, "sac_code", e.target.value)}
                                />
                              </td>
                              <td className="minselect">
                                <Select
                                  options={currencyOptions}
                                  value={currencyOptions.find((c) => c.value === invoice.inputChargeRow.currency) || null}
                                  onChange={(selected) => handleInvoiceCurrencyChange(invoice.id, null, selected?.value || "")}
                                  isLoading={loadingCurrencies}
                                  placeholder="Select"
                                />
                              </td>
                              <td>
                                <InputField
                                  type="number"
                                  placeholder="Rate"
                                  value={invoice.inputChargeRow.rate || ""}
                                  onChange={(e) => updateInvoiceInputCharge(invoice.id, "rate", e.target.value)}
                                />
                              </td>
                              <td>
                                <InputField
                                  type="number"
                                  placeholder="Quantity"
                                  value={invoice.inputChargeRow.quantity || ""}
                                  onChange={(e) => updateInvoiceInputCharge(invoice.id, "quantity", e.target.value)}
                                />
                              </td>
                              <td>
                                <InputField
                                  type="text"
                                  value={invoice.inputChargeRow.amount || 0}
                                  readOnly
                                />
                              </td>

                              <td>
                                <InputField
                                  type="text"
                                  value={invoice.inputChargeRow.amount_in_inr || 0}
                                  readOnly
                                />
                              </td>
                              {invoice.currency !== "INR" && (
                                <td>
                                  <InputField
                                    type="text"
                                    value={convertInrToInvoiceCurrency(
                                      invoice.inputChargeRow.amount_in_inr,
                                      invoice.currency
                                    )}
                                    readOnly
                                  />
                                </td>
                              )}
                              {invoice.currency === "INR" && (
                                <>
                                  <td className="minselect">
                                    <Select
                                      options={[
                                        { label: "Yes", value: "yes" },
                                        { label: "No", value: "no" },
                                      ]}
                                      value={invoice.inputChargeRow.gst_option || null}
                                      onChange={(selectedOption) => updateInvoiceInputCharge(invoice.id, "gst_option", selectedOption)}
                                      placeholder="Select"
                                    />
                                  </td>
                                  <td>
                                    <InputField
                                      type="number"
                                      value={invoice.inputChargeRow.gst_percentage || 0}
                                      onChange={(e) => updateInvoiceInputCharge(invoice.id, "gst_percentage", e.target.value)}
                                      disabled={invoice.inputChargeRow.gst_option?.value !== "yes"}
                                    />
                                  </td>
                                  <td>
                                    <InputField
                                      type="number"
                                      value={invoice.inputChargeRow.gst_amount || 0}
                                      onChange={(e) => updateInvoiceInputCharge(invoice.id, "gst_amount", e.target.value)}
                                      disabled={invoice.inputChargeRow.gst_option?.value !== "yes"}
                                    />
                                  </td>
                                </>
                              )}
                              <td className="text-center">
                                <AddCircleOutlineIcon
                                  style={{
                                    cursor: "pointer",
                                    color: "green",
                                    fontSize: 28,
                                  }}
                                  onClick={() => addInvoiceChargeRow(invoice.id)}
                                />
                              </td>
                            </tr>
                            {invCharges.map(({ charge, key }) => (
                              <tr key={key}>
                                <td>
                                  <SelectField
                                    options={chargeCodeOptions}
                                    value={charge.charge_code}
                                    onChange={(val) => {
                                      const index = parseInt(key.replace("charge_", ""), 10);
                                      handleChargeInputChange(index, "charge_code", val);
                                    }}
                                    isLoading={loadingChargeCodes}
                                    searchable
                                  />
                                </td>
                                <td>
                                  <InputField
                                    type="text"
                                    value={charge.sac_code || ""}
                                    onChange={(e) => {
                                      const index = parseInt(key.replace("charge_", ""), 10);
                                      handleChargeInputChange(index, "sac_code", e.target.value);
                                    }}
                                  />
                                </td>
                                <td className="minselect">
                                  <Select
                                    options={currencyOptions}
                                    value={currencyOptions.find((c) => c.value === charge.currency) || null}
                                    onChange={(selected) => handleInvoiceCurrencyChange(invoice.id, key, selected?.value || "")}
                                    isLoading={loadingCurrencies}
                                  />
                                </td>
                                <td>
                                  <InputField
                                    type="number"
                                    value={charge.rate || 0}
                                    onChange={(e) => {
                                      const index = parseInt(key.replace("charge_", ""), 10);
                                      handleChargeInputChange(index, "rate", e.target.value);
                                    }}
                                  />
                                </td>
                                <td>
                                  <InputField
                                    type="number"
                                    value={charge.quantity || 0}
                                    onChange={(e) => {
                                      const index = parseInt(key.replace("charge_", ""), 10);
                                      handleChargeInputChange(index, "quantity", e.target.value);
                                    }}
                                  />
                                </td>
                                <td>
                                  <InputField
                                    type="text"
                                    value={charge.amount || 0}
                                    readOnly
                                  />
                                </td>

                                <td>
                                  <InputField
                                    type="text"
                                    value={charge.amount_in_inr || 0}
                                    readOnly
                                  />
                                </td>
                                {invoice.currency !== "INR" && (
                                  <td>
                                    <InputField
                                      type="text"
                                      value={convertInrToInvoiceCurrency(
                                        charge.amount_in_inr,
                                        invoice.currency
                                      )}
                                      readOnly
                                    />
                                  </td>
                                )}
                                {invoice.currency === "INR" && (
                                  <>
                                    <td className="minselect">
                                      <Select
                                        options={[
                                          { label: "Yes", value: "yes" },
                                          { label: "No", value: "no" },
                                        ]}
                                        value={charge.gst_option || null}
                                        onChange={(selectedOption) => {
                                          const index = parseInt(key.replace("charge_", ""), 10);
                                          handleChargeInputChange(index, "gst_option", selectedOption);
                                        }}
                                        placeholder="Select"
                                      />
                                    </td>
                                    <td>
                                      <InputField
                                        type="number"
                                        value={charge.gst_percentage ?? 0}
                                        onChange={(e) => {
                                          const index = parseInt(key.replace("charge_", ""), 10);
                                          handleChargeInputChange(index, "gst_percentage", e.target.value);
                                        }}
                                        disabled={charge.gst_option?.value !== "yes"}
                                      />
                                    </td>
                                    <td>
                                      <InputField
                                        type="number"
                                        value={charge.gst_amount ?? 0}
                                        onChange={(e) => {
                                          const index = parseInt(key.replace("charge_", ""), 10);
                                          handleChargeInputChange(index, "gst_amount", e.target.value);
                                        }}
                                        disabled={charge.gst_option?.value !== "yes"}
                                      />
                                    </td>
                                  </>
                                )}
                                <td className="text-center">
                                  <DeleteOutlineIcon
                                    style={{
                                      cursor: "pointer",
                                      color: "red",
                                      fontSize: 28,
                                    }}
                                    onClick={() => removeChargeFromInvoice(invoice.id, key)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <table
                        className="table"
                        style={{
                          width: "100%",
                          maxWidth: "400px",
                        }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ textAlign: "left", fontWeight: "bold" }}>Sub Total</td>
                            <td style={{ textAlign: "right" }}>
                              {invoice.currency === "INR"
                                ? invTotals.subTotal.toFixed(2)
                                : `${invoice.currency} ${convertInrToInvoiceCurrency(
                                  invTotalsInrSubTotal,
                                  invoice.currency
                                ).toFixed(2)}`}
                            </td>
                          </tr>
                          {invoice.currency === "INR" && (
                            <tr style={{ borderTop: "2px solid #dee2e6" }}>
                              <td style={{ textAlign: "left", fontWeight: "bold" }}>GST Total</td>
                              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                {invTotals.gstAmount.toFixed(2)}
                              </td>
                            </tr>
                          )}
                          <tr className="table-secondary">
                            <td
                              style={{
                                textAlign: "left",
                                fontWeight: "bold",
                                fontSize: "1.1rem",
                              }}
                            >
                              Grand Total
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                fontSize: "1.1rem",
                              }}
                            >
                              {invoice.currency === "INR"
                                ? invTotals.grandTotal.toFixed(2)
                                : `${invoice.currency} ${convertInrToInvoiceCurrency(
                                  invTotalsInrSubTotal,
                                  invoice.currency
                                ).toFixed(2)}`}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Exchange Rates for this Invoice */}
                      <div className="mt-3">
                        {/* Invoice Currency Exchange Rate */}
                        {invoice.currency !== "INR" && (
                          <div
                            className="border rounded p-3 mb-3"
                            style={{
                              backgroundColor: "#e7f1ff",
                              border: "1px solid #b6d4fe",
                              borderRadius: "8px",
                              maxWidth: "400px",
                            }}
                          >
                            <div className="d-flex align-items-center mb-2">
                              <Tooltip
                                title="Exchange rate for converting INR totals to invoice currency. Edit applies to this form only."
                              >
                                <InfoIcon
                                  title=""
                                  sx={{
                                    fontSize: "1rem",
                                    cursor: "default",
                                    color: "#0d6efd",
                                    mr: "4px",
                                  }}
                                />
                              </Tooltip>
                              <strong style={{ fontSize: "0.875rem", color: "#0d6efd" }}>
                                Invoice Currency: {invoice.currency}
                              </strong>
                            </div>
                            {(() => {
                              const currentRate =
                                editedExchangeRates[invoice.currency] ??
                                invCharges.find(({ charge }) => charge.currency === invoice.currency)?.charge?.exchange_rate ??
                                "";
                              return (
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <strong>1 {invoice.currency}</strong>
                                  <span>=</span>
                                  <InputField
                                    type="number"
                                    value={currentRate}
                                    onChange={(e) =>
                                      handleExchangeRateChange(invoice.currency, e.target.value)
                                    }
                                    placeholder="Exchange Rate"
                                    onWheel={(e) => e.currentTarget.blur()}
                                    inpcls="cuswdt75"
                                  />
                                  <strong>INR</strong>
                                  <EditIcon
                                    sx={{
                                      fontSize: 20,
                                      color: "gray",
                                      cursor: "pointer",
                                      marginLeft: "4px",
                                    }}
                                    titleAccess={`Edit ${invoice.currency} Exchange Rate`}
                                    onClick={() => handleEditExchangeRate(invoice.currency)}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* Charge Currencies Exchange Rates */}
                        {(() => {
                          const chargeCurrencies = Array.from(
                            new Set(
                              invCharges
                                .map(({ charge }) => charge.currency)
                                .filter((c) => c && c !== "INR"),
                            ),
                          );

                          if (chargeCurrencies.length === 0) {
                            return null;
                          }

                          return (
                            <div
                              className="border rounded p-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #dee2e6",
                                borderRadius: "8px",
                                maxWidth: "400px",
                              }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <Tooltip
                                  title="Exchange rates for converting charge amounts to INR. Edit applies to this form only."
                                >
                                  <InfoIcon
                                    title=""
                                    sx={{
                                      fontSize: "1rem",
                                      cursor: "default",
                                      color: "#6c757d",
                                      mr: "4px",
                                    }}
                                  />
                                </Tooltip>
                                <strong style={{ fontSize: "0.875rem", color: "#495057" }}>
                                  Charge Currencies
                                </strong>
                              </div>
                              {chargeCurrencies.map((currency) => {
                                const chargeWithCurrency = invCharges.find(
                                  ({ charge }) => charge.currency === currency,
                                );
                                const currentRate =
                                  editedExchangeRates[currency] ??
                                  chargeWithCurrency?.charge?.exchange_rate ??
                                  "";

                                return (
                                  <div key={currency} className="mb-2">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <strong>1 {currency}</strong>
                                      <span>=</span>
                                      <InputField
                                        type="number"
                                        value={currentRate}
                                        onChange={(e) =>
                                          handleExchangeRateChange(currency, e.target.value)
                                        }
                                        placeholder="Exchange Rate"
                                        onWheel={(e) => e.currentTarget.blur()}
                                        inpcls="cuswdt75"
                                      />
                                      <strong>INR</strong>
                                      <EditIcon
                                        sx={{
                                          fontSize: 20,
                                          color: "gray",
                                          cursor: "pointer",
                                          marginLeft: "4px",
                                        }}
                                        titleAccess={`Edit ${currency} Exchange Rate`}
                                        onClick={() => handleEditExchangeRate(currency)}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {canAddMultipleInvoices(formData.freight_terms) && !isSingleInvoiceEdit && (
              <div className="d-flex justify-content-start mt-3">
                <Button
                  variant="contained"
                  btncls="bg-darkblue text-white px-4 py-2 vendor-add-btn"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addInvoice}
                >
                  Add Invoice
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Term & Conditions */}
        <div className="row mt-3">
          <div className="col-md-4">
            <SelectField
              label="Term & Conditions"
              options={[
                "LCL IMPORT - NON HAZ CARGO",
                "LCL IMPORT - HAZ CARGO",
                "LCL EXPORT - NON HAZ CARGO",
                "LCL EXPORT- HAZ CARGO",
                "FCL IMPORT - NON HAZ CARGO ( Dock Stuffing )",
                "FCL IMPORT - NON HAZ CARGO ( Factory Stuffing )",
                "FCL IMPORT - HAZ CARGO ( Dock Stuffing )",
                "FCL IMPORT - HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- NON HAZ CARGO ( Dock Stuffing )",
                "FCL EXPORT- NON HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- HAZ CARGO ( Dock Stuffing )",
                "FCL EXPORT- HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- Russia / CIS Sector Via Iran",
              ]}
              value={formData.term_and_condition}
              onChange={(val) => handleFormChange("term_and_condition", val)}
              error={errors.term_and_condition}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-between my-3 gap-3 align-items-center">
          <Button
            variant="darkblue"
            btncls="px-4"
            type="button"
            startIcon={<SaveIcon />}
            children={
              editSaleInvoiceId
                ? "Update Performa Invoice"
                : "Create Performa Invoice"
            }
            disabled={loading || loadingSubmit}
            onClick={() => {
              if (!validate()) return;
              if (
                !editSaleInvoiceId &&
                needsCollectSplit(formData.freight_terms)
              ) {
                const allKeys = chargeRow.map((_, i) => chargeRowKey(i));
                const assignedKeys = getAllAssignedKeys();
                const unassigned = allKeys.filter(
                  (k) => !assignedKeys.includes(k),
                );
                if (unassigned.length > 0) {
                  setErrors((prev) => ({
                    ...prev,
                    invoiceSplit:
                      "Please assign all charges to invoices before proceeding.",
                  }));
                  return;
                }
                if (invoices.length < 2) {
                  setErrors((prev) => ({
                    ...prev,
                    invoiceSplit:
                      "Please create at least 2 invoices for Collect freight terms.",
                  }));
                  return;
                }
                const emptyInvoice = invoices.find(
                  (inv) => !inv.charge_keys || inv.charge_keys.length === 0,
                );
                if (emptyInvoice) {
                  const idx = invoices.indexOf(emptyInvoice) + 1;
                  setErrors((prev) => ({
                    ...prev,
                    invoiceSplit: `Invoice ${idx} has no charges.`,
                  }));
                  return;
                }
              } else if (isSingleInvoiceEdit) {
                // Single invoice edit: validate only the target invoice has charges
                const targetInvoice = invoices[0];
                if (!targetInvoice.charge_keys || targetInvoice.charge_keys.length === 0) {
                  setErrors((prev) => ({
                    ...prev,
                    invoiceSplit: "This invoice has no charges. Please add at least one charge.",
                  }));
                  return;
                }
              }
              saveDraftForPreview();
              navigate(
                id && id !== "new"
                  ? `/create-performa-invoice/${id}`
                  : "/create-performa-invoice",
              );
            }}
          />
          <Button
            variant="darkblue"
            btncls="px-4"
            type="button"
            startIcon={<SaveIcon />}
            children="Create Final Invoice"
            // Final invoices are issued by the Tally integration, not created
            // here. Button is kept visible for the future flow but is inert.
            title="Final Invoice will be created automatically once the Tally integration is live"
            disabled={loading || loadingSubmit}
            onClick={() => { }}
          />
          {/* Hidden submit button to capture Enter key submission (Performa) */}
          <button type="submit" style={{ display: "none" }} />
        </div>
      </form>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Confirm {invoiceType === "performa" ? "Performa" : "Final"}{" "}
                  Invoice Creation
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirm(false)}
                  disabled={loadingSubmit}
                />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to create this {invoiceType} invoice?
                </p>
                <p className="mb-0">
                  <strong>SubTotal:</strong> {totals.subTotal} |
                  <strong> GST:</strong> {totals.gstAmount} |
                  <strong> Total:</strong> {totals.grandTotal}
                </p>
              </div>
              <div className="modal-footer">
                <Button
                  className="btn btn-secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={loadingSubmit}
                  text="No"
                />
                <Button
                  className="btn btn-primary"
                  onClick={submitInvoice}
                  disabled={loadingSubmit}
                  text={
                    loadingSubmit
                      ? "Creating..."
                      : `Yes, Create ${invoiceType === "performa" ? "Performa" : "Final"} Invoice`
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-success">Success</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSuccess(false)}
                />
              </div>
              <div className="modal-body">
                <p>
                  {invoiceType === "performa" ? "Performa" : "Final"} invoice
                  created successfully!
                </p>
              </div>
              <div className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowSuccess(false);
                    navigate("/billingAndInvoice/invoicing");
                  }}
                  text="Ok"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charge Selection Modal */}
      {showChargeSelectModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Charges from Quotation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => { setShowChargeSelectModal(false); setChargeSelectError(""); }}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">Select charges to add to this invoice. Charges already assigned to another invoice are disabled.</p>
                <div className="border rounded p-2" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {quotationCharges.length === 0 ? (
                    <p className="text-muted mb-0">No quotation charges available.</p>
                  ) : (
                    quotationCharges.map((row, index) => {
                      const key = chargeRowKey(index);
                      const currentInvoice = invoices.find(inv => inv.id === chargeSelectInvoiceId);
                      const isInCurrentInvoice = currentInvoice?.charge_keys?.includes(key);
                      const isAssignedElsewhere = isChargeAssigned(key) && !isInCurrentInvoice;
                      return (
                        <div className="form-check" key={key}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`charge-${key}`}
                            checked={isInCurrentInvoice}
                            disabled={isAssignedElsewhere}
                            onChange={() => {
                              if (isInCurrentInvoice) {
                                removeChargeFromInvoice(chargeSelectInvoiceId, key);
                              } else {
                                addChargeToInvoice(chargeSelectInvoiceId, key);
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`charge-${key}`}>
                            {collectChargeLabel(row)}
                            {isAssignedElsewhere && <span className="text-muted ms-2">(assigned to another invoice)</span>}
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                {chargeSelectError && <div className="text-danger mt-2">{chargeSelectError}</div>}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowChargeSelectModal(false); setChargeSelectError(""); }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {(showConfirm || showSuccess) && (
        <div className="modal-backdrop fade show"></div>
      )}
      <ExchangeRateEditModal
        show={showExchangeRateModal}
        currency={selectedExchangeCurrency}
        value={exchangeRateModalValue}
        onChange={setExchangeRateModalValue}
        onClose={() => setShowExchangeRateModal(false)}
        onSave={handleSaveExchangeRate}
      />
    </>
  );
};

export default InvoicingForm;
